import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { UtilisateurService } from 'src/app/core/services/utilisateur.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { ToastService } from 'src/app/core/services/toast.service';

@Component({
  selector: 'app-listeutilisateurs',
  templateUrl: './liste-utilisateurs.component.html',
  styleUrls: ['./liste-utilisateurs.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ListeUtilisateursComponent implements OnInit {

  get colonnesAffichees(): string[] {
    if (this.scope === 'gestionnaires') {
      return ['nom', 'prenom', 'telephone', 'zones', 'role', 'actions'];
    }
    return ['nom', 'prenom', 'email', 'telephone', 'typeUtilisateur', 'role', 'noteMoyenne', 'actions'];
  }
  sourceDonnees: MatTableDataSource<any>;
  notationsParLivreur: Record<number, { moyenneNote: number; nombreEvaluations: number }> = {};
  toutesDonnees: any[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  indicateurChargement = true;
  totalEnregistrements: number = 0;
  scope: string = 'tous';
  titrePage = 'Liste des Utilisateurs';

  constructor(
    public utilisateurService: UtilisateurService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) {
    this.sourceDonnees = new MatTableDataSource<any>([]);
  }

  get nbActifs(): number { return this.sourceDonnees.data.filter(u => u.enabled !== false).length; }
  get nbInactifs(): number { return this.sourceDonnees.data.filter(u => u.enabled === false).length; }
  get nbLivreursScope(): number { return this.sourceDonnees.data.filter(u => (u?.role || '').toString().toUpperCase() === 'LIVREUR').length; }

  get estResponsableZone(): boolean {
    return (this.authService.infosUtilisateur?.role || '').toString().toUpperCase() === 'RESPONSABLE_ZONE';
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.scope = params.get('scope') || 'tous';
      this.titrePage = this.construireTitre();
      if (this.estResponsableZone) {
        // Re-appliquer le scope sur les données déjà chargées si elles existent
        if (this.toutesDonnees.length > 0) {
          const filtres = this.appliquerScope(this.toutesDonnees);
          this.sourceDonnees.data = filtres;
          this.totalEnregistrements = filtres.length;
        } else {
          this.chargerMembresZone();
        }
      } else {
        const filtres = this.appliquerScope(this.toutesDonnees || []);
        this.sourceDonnees.data = filtres;
        this.totalEnregistrements = filtres.length;
      }
    });
    if (this.estResponsableZone) {
      this.chargerMembresZone();
    } else {
      this.chargerDonnees();
      this.utilisateurService.obtenirUtilisateursActifs(0, 500);
    }
  }

  chargerMembresZone(): void {
    this.indicateurChargement = true;
    this.utilisateurService.obtenirMembresDeMonZone().subscribe({
      next: (donnees: any[]) => {
        this.toutesDonnees = donnees || [];
        const filtres = this.appliquerScope(this.toutesDonnees);
        this.sourceDonnees.data = filtres;
        this.totalEnregistrements = filtres.length;
        this.indicateurChargement = false;
        this.chargerMoyennesLivreurs();
      },
      error: () => {
        this.toast.erreur('Impossible de charger les membres de votre zone');
        this.indicateurChargement = false;
      }
    });
  }

  ngAfterViewInit() {
    this.sourceDonnees.paginator = this.paginator;
    this.sourceDonnees.sort = this.sort;
  }

  chargerDonnees() {
    this.utilisateurService.donnees$.subscribe(
      donnees => {
        this.toutesDonnees = donnees || [];
        const filtres = this.appliquerScope(donnees || []);
        this.sourceDonnees.data = filtres;
        this.totalEnregistrements = filtres.length;
        this.indicateurChargement = false;
        this.chargerMoyennesLivreurs();
      },
      erreur => {
        console.error('Erreur lors de la récupération des données', erreur);
        this.indicateurChargement = false;
      }
    );
  }

  chargerMoyennesLivreurs(): void {
    this.utilisateurService.obtenirMoyennesNotationsLivreurs().subscribe({
      next: (rows: any[]) => {
        const map: Record<number, { moyenneNote: number; nombreEvaluations: number }> = {};
        for (const r of rows || []) {
          const id = Number(r?.livreurId);
          if (!id) continue;
          map[id] = {
            moyenneNote: Number(r?.moyenneNote ?? 0),
            nombreEvaluations: Number(r?.nombreEvaluations ?? 0)
          };
        }
        this.notationsParLivreur = map;
      },
      error: () => {
        this.notationsParLivreur = {};
      }
    });
  }

  appliquerFiltre(event: Event) {
    const valeurFiltre = (event.target as HTMLInputElement).value;
    this.sourceDonnees.filter = valeurFiltre.trim().toLowerCase();
    if (this.sourceDonnees.paginator) {
      this.sourceDonnees.paginator.firstPage();
    }
  }

  ajouterUtilisateur() {
    this.router.navigate(['/utilisateur/enregistrerutilisateur'], {
      queryParams: { returnUrl: this.router.url }
    });
  }

  estClient(user: any): boolean {
    const type = this.extraireTypeUtilisateur(user);
    return type.includes('CLIENT');
  }

  creerCommandePourClient(user: any): void {
    this.router.navigate(['/commande/enregistrer'], { queryParams: { mode: 'urbain', clientId: user?.id } });
  }

  voirDetailClient(user: any): void {
    this.router.navigate(['/clients', user?.id]);
  }

  modifierUtilisateur(id: number) {
    this.router.navigate(['/utilisateur/enregistrerutilisateur', id], {
      queryParams: { returnUrl: this.router.url }
    });
  }

  supprimerUtilisateur(utilisateur: any) {
    this.toast.confirmer(
      `Êtes-vous sûr de vouloir supprimer l'utilisateur "${utilisateur.prenom} ${utilisateur.nom}" ?`,
      'Confirmation de suppression'
    ).subscribe(confirme => {
      if (confirme) {
        this.utilisateurService.supprimerUtilisateur(utilisateur.id).subscribe({
          next: () => {
            this.toast.succes('Utilisateur supprimé avec succès', 'Succès');
            const donneesMaj = this.sourceDonnees.data.filter(u => u.id !== utilisateur.id);
            this.sourceDonnees.data = donneesMaj;
            this.totalEnregistrements = donneesMaj.length;
            if (this.paginator) {
              this.paginator.length = this.totalEnregistrements;
              const pageIndex = this.paginator.pageIndex;
              const pageSize = this.paginator.pageSize;
              const nbPages = Math.ceil(this.totalEnregistrements / pageSize);
              if (pageIndex >= nbPages && pageIndex > 0) {
                this.paginator.previousPage();
              }
            }
            this.utilisateurService.obtenirUtilisateursActifs(0, 500);
          },
          error: (err) => {
            this.toast.erreur(err?.error?.message || 'Erreur lors de la suppression', 'Erreur');
          }
        });
      }
    });
  }

  obtenirClasseBadgeRole(role: string): string {
    const classesRole: { [cle: string]: string } = {
      'ADMIN': 'badge-admin',
      'MANAGER': 'badge-manager',
      'USER': 'badge-user',
      'LIVREUR': 'badge-livreur',
      'COMPTABLE_ZONE': 'badge-manager',
      'COMPTABLE_GENERAL': 'badge-admin'
    };
    return classesRole[role] || 'badge-default';
  }

  estLivreur(user: any): boolean {
    return (user?.role || '').toString().toUpperCase() === 'LIVREUR';
  }

  obtenirMoyenneLivreur(user: any): string {
    if (!this.estLivreur(user)) return '—';
    const rating = this.notationsParLivreur[user?.id];
    if (!rating || rating.nombreEvaluations <= 0) return '—';
    return `${rating.moyenneNote.toFixed(1)} / 5`;
  }

  obtenirNbAvisLivreur(user: any): number {
    if (!this.estLivreur(user)) return 0;
    return this.notationsParLivreur[user?.id]?.nombreEvaluations ?? 0;
  }

  private construireTitre(): string {
    const titres: Record<string, string> = {
      tous: 'Liste des Utilisateurs',
      dematral: 'Équipe Dematral',
      livreurs: 'Liste des Livreurs',
      gestionnaires: 'Responsables de zone',
      comptables: 'Comptables',
      clients: 'Clients',
      'clients-standards': 'Clients standards',
      'clients-contrat': 'Clients sous contrat'
    };
    return titres[this.scope] ?? 'Liste des Utilisateurs';
  }

  private appliquerScope(donnees: any[]): any[] {
    switch (this.scope) {
      case 'dematral':
        return donnees.filter((u: any) => this.extraireTypeUtilisateur(u).includes('DEMATRAL'));
      case 'livreurs':
        return donnees.filter((u: any) => (u?.role || '').toString().toUpperCase() === 'LIVREUR');
      case 'gestionnaires':
        return donnees.filter((u: any) => {
          const role = (u?.role || '').toString().toUpperCase();
          return role === 'RESPONSABLE_ZONE' || role === 'GESTIONNAIRE_ZONE';
        });
      case 'comptables':
        return donnees.filter((u: any) => ['COMPTABLE_ZONE', 'COMPTABLE_GENERAL'].includes((u?.role || '').toString().toUpperCase()));
      case 'clients':
        return donnees.filter((u: any) => this.estClient(u));
      case 'clients-standards':
        return donnees.filter((u: any) => this.extraireTypeUtilisateur(u).includes('CLIENT STAND'));
      case 'clients-contrat':
        return donnees.filter((u: any) => this.extraireTypeUtilisateur(u).includes('CLIENT SOUS CONTRAT'));
      default:
        return donnees;
    }
  }

  private extraireTypeUtilisateur(user: any): string {
    return (user?.typeUtilisateurLibelle || user?.typeUtilisateur || '').toString().toUpperCase();
  }

  obtenirZonesLibelle(user: any): string {
    if (Array.isArray(user?.zoneNoms) && user.zoneNoms.length > 0) {
      return user.zoneNoms.join(', ');
    }
    return user?.zoneResponsableNom || '—';
  }

  obtenirZonesTags(user: any): string[] {
    if (Array.isArray(user?.zoneNoms) && user.zoneNoms.length > 0) return user.zoneNoms;
    if (user?.zoneResponsableNom) return [user.zoneResponsableNom];
    return [];
  }
}
